"use client";

import * as React from "react";
import { Table } from "@tanstack/react-table";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useListContext, useRefresh } from "react-admin";
import { DateRange } from "react-day-picker";

// Components
import OrderDetails from "./OrderDetails";
import OrdersListHeader from "@/components/common/orders/OrdersListHeader";
import { DataTable } from "@/components/data-table/data-table";
import { OrderTableFilters } from "./components/OrderTableFilters";

// Hooks
import { useOrderColumns } from "./hooks/useOrderColumns";
import { useStatusManagement } from "./hooks/useStatusManagement";
import { useOrderData } from "./hooks/useOrderData";
import { endOfDay, startOfDay } from "date-fns";
import { useLocation } from "react-router-dom";
import { Orders, Settings } from "@/interface/entityInterface";
import axiosInstance from "@/utils/axiosInstance";
import { useUserContext } from "@/context/UserContext";

const OrderList = () => {
  const {decodedToken} = useUserContext()
  const { state } = useLocation();
  const refresh = useRefresh();
  const [configs, setConfigs] = React.useState<{
    vehicle_assign: boolean;
    driver_assign: boolean;
  }>({ vehicle_assign: false, driver_assign: false });

  const {
    data = [],
    total = 0,
    page = 1,
    perPage = 50,
    setPage,
    setPerPage,
    setFilters,
    filterValues,
    sort = {
      field: "pickup_time",
      order: "DESC",
    },
    setSort,
  } = useListContext();

  // Local state
  const [selectedOrder, setSelectedOrder] = React.useState<Orders | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [filteredVehicles, setFilteredVehicles] = React.useState<string[]>([]);
  const [filteredDrivers, setFilteredDrivers] = React.useState<string[]>([]);
  const [sources, setSources] = React.useState<string[]>([]);

  const [clientFilter, setClientFilter] = React.useState<string>("");
  const [pickupFilter, setPickupFilter] = React.useState<string>("");

  const [dateRange, setDateRange] = React.useState<DateRange>({
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
  });

  // Custom hooks
  const { drivers, vehicles } = useOrderData();
  const {
    statusMenu,
    statusSummary,
    totalOrdersCount,
    filteredOrdersCount,
    selectedStatus,
    statusFilter,
    handleFilterChange,
    updateStatusFilter,
    resetStatusFilter,
    updateStatusMenu,
  } = useStatusManagement(dateRange);

  React.useEffect(() => {
    const getSettings = async () => {
      const {data} = await axiosInstance.get<Settings>(`/settings/${decodedToken?.settings.id}`)
      const {show_vehicle_on_order_assign, show_driver_on_order_assign} = data
      setConfigs({vehicle_assign: show_vehicle_on_order_assign || false , driver_assign: show_driver_on_order_assign || false })
    }

    getSettings()
  }, [])

  const columns = useOrderColumns({
    configs,
    vehicles,
    drivers,
  });

  // Event handlers
  const handleRowClick = (rowData: Orders) => {
    setSelectedOrder(rowData);
    setIsSheetOpen(true);
  };

  const handleRefresh = async () => {
    await updateStatusMenu();
    refresh();
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const handleStatusFilterChange = (selected: string[]) => {
    updateStatusFilter(selected);

    setFilters({ ...filterValues, status: selected });
  };

  const handleVehicleFilterChange = (selected: string[]) => {
    setFilteredVehicles(selected);
    setFilters({ ...filterValues, vehicle: selected });
  };

  const handleDriverFilterChange = (selected: string[]) => {
    setFilteredDrivers(selected);
    setFilters({ ...filterValues, driverId: selected });
  };

  const handleSourceFilterChange = (selected: string[]) => {
    setSources(selected);
    setFilters({ ...filterValues, sources: selected });
  };

  const handleResetFilters = () => {
    resetStatusFilter();
    setFilteredVehicles([]);
    setClientFilter("");
    setPickupFilter("");
    setSources([]);
    const currentStatus = selectedStatus === "ALL" ? [] : [selectedStatus];

    setFilters({
      status: currentStatus,
      pickupTime: dateRange.from,
      endPickupTime: dateRange.to,
    });
  };

  React.useEffect(() => {
    if (state?.client_phone) setClientFilter(state.client_phone);
  }, [state]);

  // Effects
  React.useEffect(() => {
    const currentStatus =
      selectedStatus === "ALL" ? statusFilter : [selectedStatus];

    setFilters({
      status: currentStatus,
      pickupTime: dateRange.from,
      endPickupTime: dateRange.to,
      vehicle: filteredVehicles,
      driverId: filteredDrivers,
      clientDetails: clientFilter,
      pickupOrDropoff: pickupFilter,
      sources,
    });
  }, [dateRange, selectedStatus, statusFilter]);

  // Debounced effect for clientFilter
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const currentStatus =
        selectedStatus === "ALL" ? statusFilter : [selectedStatus];

      setFilters({
        status: currentStatus,
        pickupTime: dateRange.from,
        endPickupTime: dateRange.to,
        vehicle: filteredVehicles,
        driverId: filteredDrivers,
        clientDetails: clientFilter,
        pickupOrDropoff: pickupFilter,
        sources,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [clientFilter, pickupFilter]);

  // Render toolbar filters
  const renderToolbarFilters = (table: Table<any>) => (
    <OrderTableFilters
      table={table}
      dateRange={dateRange}
      setDateRange={handleDateRangeChange}
      selectedStatus={selectedStatus}
      selectedVehicles={filteredVehicles}
      selectedDrivers={filteredDrivers}
      clientFilter={clientFilter}
      pickupFilter={pickupFilter}
      statusSummary={statusSummary}
      statusFilter={statusFilter}
      vehicles={vehicles}
      drivers={drivers}
      sources={sources}
      onStatusFilterChange={handleStatusFilterChange}
      onVehicleFilterChange={handleVehicleFilterChange}
      onDriverFilterChange={handleDriverFilterChange}
      onClientFilterChange={setClientFilter}
      onPickupFilterChange={setPickupFilter}
      onSourceChange={handleSourceFilterChange}
      assignByDriver={configs.driver_assign}
    />
  );

  return (
    <div>
      <OrdersListHeader
        selectedTab={selectedStatus}
        handleFilterChange={(status) => {
          handleFilterChange(status);
          const currentStatus = status === "ALL" ? statusFilter : [status];
          setFilters({
            ...filterValues,
            status: currentStatus,
            pickupTime: dateRange.from,
            endPickupTime: dateRange.to,
          });
        }}
        statusMenu={statusMenu}
        totalOrders={totalOrdersCount}
      />

      <div className="mt-6" />

      <DataTable
        data={data}
        columns={columns}
        isCustomFiltered={
          statusFilter.length > 0 ||
          filteredVehicles.length > 0 ||
          clientFilter?.length > 0 ||
          pickupFilter?.length > 0 ||
          filteredDrivers?.length > 0
        }
        pagination={{
          page,
          perPage,
          setPage,
          setPerPage,
          total,
        }}
        sorting={{
          sort,
          setSort,
        }}
        onRowClick={handleRowClick}
        renderToolbarFilters={renderToolbarFilters}
        onResetFilters={handleResetFilters}
        rightActions={
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw />
          </Button>
        }
      />

      {selectedOrder && (
        <OrderDetails
          selectedOrder={selectedOrder}
          isSheetOpen={isSheetOpen}
          setIsSheetOpen={setIsSheetOpen}
        />
      )}
    </div>
  );
};

export default OrderList;
